import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/solid';
import { MLInvitationService } from '../services/mlInvitationService';
import { useAuth } from '../contexts/AuthContext';

type InvitationStatus = 'loading' | 'valid' | 'expired' | 'used' | 'cancelled' | 'invalid';

interface StateConfig {
  icon: React.ReactNode;
  title: string;
  message: string;
  bgColor: string;
  iconColor: string;
  cardBorder: string;
}

const stateConfigs: Record<InvitationStatus, StateConfig> = {
  loading: {
    icon: (
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto" />
    ),
    title: 'Validating invitation...',
    message: 'Please wait while we verify your invitation link.',
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    cardBorder: 'border-blue-200',
  },
  valid: {
    icon: <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />,
    title: 'Invitation Valid',
    message: 'Your invitation is valid. Redirecting you to registration...',
    bgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    cardBorder: 'border-green-200',
  },
  expired: {
    icon: <ClockIcon className="h-16 w-16 text-orange-500 mx-auto" />,
    title: 'Invitation Expired',
    message: 'This invitation has expired. Please contact support for assistance.',
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-600',
    cardBorder: 'border-orange-200',
  },
  used: {
    icon: <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto" />,
    title: 'Invitation Already Used',
    message: 'This invitation has already been used. Each invitation can only be used once.',
    bgColor: 'bg-yellow-50',
    iconColor: 'text-yellow-600',
    cardBorder: 'border-yellow-200',
  },
  cancelled: {
    icon: <XCircleIcon className="h-16 w-16 text-red-500 mx-auto" />,
    title: 'Invitation Cancelled',
    message: 'This invitation has been cancelled. Please contact support for assistance.',
    bgColor: 'bg-red-50',
    iconColor: 'text-red-600',
    cardBorder: 'border-red-200',
  },
  invalid: {
    icon: <ExclamationCircleIcon className="h-16 w-16 text-red-500 mx-auto" />,
    title: 'Invalid Invitation',
    message: 'This invitation link is invalid or could not be found. Please check the URL and try again.',
    bgColor: 'bg-red-50',
    iconColor: 'text-red-600',
    cardBorder: 'border-red-200',
  },
};

const MLInvitationLandingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<InvitationStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const validateInvitation = async () => {
      if (!token) {
        setStatus('invalid');
        setError('No invitation token provided');
        return;
      }

      try {
        const result = await MLInvitationService.getInvitationByToken(token);

        if (!result.success || !result.data) {
          setStatus('invalid');
          setError(result.error || 'Invalid invitation');
          return;
        }

        const invitation = result.data;

        // Map invitation status to page state
        switch (invitation.status) {
          case 'registered':
            if (user?.email && invitation.email && user.email.toLowerCase() === invitation.email.toLowerCase()) {
              navigate('/dashboard/ml');
              return;
            }
            setStatus('used');
            break;
          case 'expired':
            setStatus('expired');
            break;
          case 'cancelled':
            setStatus('cancelled');
            break;
          case 'pending':
            setStatus('invalid');
            setError('This invitation has not been sent yet.');
            break;
          case 'sent':
          case 'viewed':
            // Valid invitation - mark as viewed and redirect
            setStatus('valid');
            
            // Mark as viewed if not already
            if (invitation.status === 'sent') {
              await MLInvitationService.markAsViewed(token);
            }

            // Redirect after 2 seconds
            setTimeout(() => {
              navigate(`/license/register/ml?invite=${encodeURIComponent(token)}`);
            }, 2000);
            break;
          default:
            setStatus('invalid');
            setError('Unknown invitation status');
        }
      } catch (err) {
        console.error('Error validating invitation:', err);
        setStatus('invalid');
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      }
    };

    validateInvitation();
  }, [token, navigate, user]);

  const config = stateConfigs[status];

  return (
    <div className={`min-h-screen flex items-center justify-center ${config.bgColor} px-4 py-12`}>
      <div className={`w-full max-w-md bg-white rounded-lg shadow-sm border ${config.cardBorder} p-8 text-center`}>
        <div className="mb-6">{config.icon}</div>
        <h1 className={`text-2xl font-bold mb-3 ${config.iconColor}`}>
          {config.title}
        </h1>
        <p className="text-gray-600 mb-4">{config.message}</p>
        
        {error && status !== 'loading' && (
          <p className="text-sm text-red-500 mt-2">{error}</p>
        )}

        {status === 'valid' && (
          <p className="text-sm text-gray-500 mt-4">
            Redirecting in 2 seconds...
          </p>
        )}

        {(status === 'invalid' || status === 'expired' || status === 'cancelled' || status === 'used') && (
          <button
            onClick={() => navigate('/auth')}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Go to Home
          </button>
        )}
      </div>
    </div>
  );
};

export default MLInvitationLandingPage;
